import { useQueryClient } from "@tanstack/react-query";
/**
 * Realtime clinic events via SSE. EventSource can't set headers, so instead
 * of leaking the access token in the URL we first POST /events/ticket (normal
 * Bearer auth via apiFetch) to mint a short-lived single-use ticket, then
 * connect with ?ticket=... — the ticket is consumed server-side on first use,
 * so proxy logs never contain a usable credential.
 *
 * Purely additive: the existing polling stays as a fallback, so a dropped or
 * unsupported stream degrades gracefully. Reconnects with capped backoff and
 * mints a fresh ticket on every (re)connect.
 */
import { useEffect } from "react";
import { useAuthStore } from "../stores/auth-store.ts";
import { apiFetch } from "./api.ts";

const API_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

export function useClinicEvents(): void {
	const qc = useQueryClient();
	const token = useAuthStore((s) => s.accessToken);

	useEffect(() => {
		if (!token) return;
		let es: EventSource | null = null;
		let stopped = false;
		let retry = 0;
		let timer: ReturnType<typeof setTimeout> | undefined;

		const scheduleReconnect = () => {
			if (stopped) return;
			retry += 1;
			const delay = Math.min(30_000, 1000 * 2 ** Math.min(retry, 5));
			timer = setTimeout(connect, delay);
		};

		const connect = async () => {
			if (stopped) return;
			let ticket: string;
			try {
				// One-time ticket (60s TTL). apiFetch handles Bearer auth + the
				// transparent refresh-on-401 dance.
				const minted = await apiFetch<{ ticket: string }>("/events/ticket", {
					method: "POST",
				});
				ticket = minted.ticket;
			} catch {
				scheduleReconnect();
				return;
			}
			if (stopped) return;

			es = new EventSource(
				`${API_URL}/events/stream?ticket=${encodeURIComponent(ticket)}`,
			);

			es.addEventListener("clinic", () => {
				qc.invalidateQueries({ queryKey: ["notifications"] });
				qc.invalidateQueries({ queryKey: ["critical-alerts"] });
				// Doctor's results inbox (page + nav badge) — new lab values /
				// finalized reports surface without waiting for the 60s poll.
				qc.invalidateQueries({ queryKey: ["results-inbox"] });
				qc.invalidateQueries({ queryKey: ["results-inbox-counts"] });
			});

			es.addEventListener("open", () => {
				retry = 0;
			});

			// EventSource's built-in auto-reconnect would reuse the same (already
			// redeemed) ticket and fail — so we close it and reconnect ourselves
			// with a freshly minted ticket.
			es.onerror = () => {
				es?.close();
				scheduleReconnect();
			};
		};

		void connect();

		return () => {
			stopped = true;
			if (timer) clearTimeout(timer);
			es?.close();
		};
	}, [token, qc]);
}
